"use client";

import React from "react";
import { Lightbulb } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const packages = [{
  name: "LITE",
  price: "£69",
  description: "Essential missed call recovery for sole traders.",
  features: ["Missed Call Text-Back", "Unified WhatsApp/Instant WhatsApp Recovery Inbox", "Google Business Sync", "No Long-Term Contracts"],
  cta: "Start Lite",
  popular: false,
  checkoutUrl: "https://thinklocalpro.com/lite-checkout"
}, {
  name: "GROWTH",
  price: "£149",
  description: "Scale your trade firm with full automation.",
  features: ["Everything in LITE", "Automated Trade Invoicing", "Review Generation System", "500 WhatsApp Credits", "Priority UK Support"],
  cta: "Secure Spot",
  popular: true,
  checkoutUrl: "https://thinklocalpro.com/growth-checkout"
}, {
  name: "AUTHORITY",
  price: "£499",
  description: "Gloucester‑based Think Local SEO agency providing hands‑on local SEO management with deep knowledge of the Gloucester, Cheltenham, and Stroud markets, giving clients a \"home‑turf\" advantage on Google Maps.",
  features: ["Everything in GROWTH", "Full SEO & Map Management", "Local Map Dominance Strategy", "Dedicated Account Manager", "Custom Lead Automations"],
  cta: "Go Pro",
  popular: false,
  checkoutUrl: "https://thinklocalpro.com/authority-checkout"
}];
export default function GrowthPackages() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="7556" id="choose-your-growth-path" ref={ref} className="bg-background relative w-full py-20 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className={cn("mb-16 text-center transition-all duration-700 delay-100", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          
          <h3 className="text-foreground font-serif text-4xl md:text-6xl font-extrabold mb-6 tracking-tight uppercase">
            Choose Your Growth Path
          </h3>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg md:text-xl font-medium">
            Simple, no-contract plans built for the tools. Automate your admin and 
            never miss a high-value lead again.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start max-w-7xl mx-auto">
          {packages.map((pkg, i) => {
          const isGrowth = pkg.popular;
          return <div key={pkg.name} data-index={i} className={cn("relative transition-all duration-700", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", i === 0 && "delay-200", i === 1 && "delay-300", i === 2 && "delay-400")}>
                {isGrowth && <div className="absolute -top-4 left-0 right-0 flex justify-center z-20">
                    <Badge className="bg-foreground text-background hover:bg-foreground px-4 py-1 text-sm font-bold shadow-md uppercase tracking-widest border-none">
                      Most Popular
                    </Badge>
                  </div>}

                <Card className={cn("relative h-full flex flex-col border-2 transition-all duration-300 group", i === 0 ? "bg-[#2D3747] border-slate-600" : i === 1 ? "bg-[#FF8C00] border-[#FFD700]/50" : "bg-[#1A1A1A] border-[#FF8C00]", isGrowth ? "shadow-2xl lg:-mt-8 lg:mb-8 z-10 scale-100 lg:scale-105" : "hover:border-[#FF8C00]/50", "hover:scale-[1.02] lg:hover:scale-[1.07] rounded-md")}>
                  <CardHeader className={cn("pb-8", isGrowth ? "pt-10" : "pt-8")}>
                    <CardTitle className={cn("text-2xl font-extrabold tracking-tighter uppercase", isGrowth ? "text-[#1A1A1A]" : "text-white")}>
                      {pkg.name}
                    </CardTitle>
                    <div className="mt-4 flex items-baseline gap-1">
                      <span className={cn("text-5xl font-extrabold font-serif", isGrowth ? "text-[#1A1A1A]" : "text-white")}>
                        {pkg.price}
                      </span>
                      <span className={cn("font-bold uppercase text-xs tracking-widest", isGrowth ? "text-[#1A1A1A]/70" : "text-slate-400")}>
                        /mo
                      </span>
                    </div>
                    <CardDescription className={cn("mt-2 text-base font-medium", isGrowth ? "text-[#1A1A1A]/80" : "text-slate-300")}>
                      {pkg.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-1">
                    <ul className="space-y-4">
                      {pkg.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3">
                          <div className={cn("mt-1 rounded-sm p-0.5", isGrowth ? "bg-[#1A1A1A]" : "bg-[#1A1A1A]/40")}>
                            <Lightbulb className="h-3.5 w-3.5 stroke-[2.5px] text-[#FF8C00]" />
                          </div>
                          <span className={cn("text-sm font-semibold leading-tight", isGrowth ? "text-[#1A1A1A]" : "text-white/90")}>
                            {feature}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className={cn("pt-8", isGrowth ? "pb-10" : "pb-8")}>
                    <Button asChild id={`${pkg.name.toLowerCase()}-cta`} className={cn("w-full text-base font-extrabold h-14 transition-all uppercase tracking-widest rounded-md", isGrowth ? "bg-[#1A1A1A] text-white hover:bg-[#1A1A1A]/90 shadow-xl" : i === 0 ? "bg-white text-[#FF8C00] hover:bg-white/90" : "bg-[#FF8C00] text-white hover:bg-[#FF8C00]/90")} size="lg">
                      <Link to={pkg.checkoutUrl}>
                        {pkg.cta}
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-16 text-center">
          
          <Button variant="outline" asChild className="border-[#FF8C00] border-2 hover:bg-[#FF8C00] hover:text-white text-[#FF8C00] font-bold uppercase tracking-widest h-12 px-8 transition-colors">
            
          </Button>
        </div>
      </div>
    </section>;
}